<template>
  <div :class="{clickable: featureSwitchesState.REFERRAL_DETAILS}"
       class="referral-list-item"
       @click="handleReferralClick">
    <need-review-circle v-if="item.need_review && featureSwitchesState.REFERRAL_DETAILS"></need-review-circle>
    <div class="line is-flex">
      <p v-if="featureSwitchesState.REFERRAL_DETAILS" class="paragraph-bold full-name">{{ item.full_name }}</p>
      <p v-else class="full-name">{{ item.full_name }}</p>

      <div class="token-amounts paragraph-s">
        <token-text
          :type="lastGrantedTokenType"
          :text="earnedTokenAmount"
          :classes="'text-inline paragraph-bold'">
        </token-text>
        <span v-if="!isOrangeGranted" class="paragraph-gray">/</span>
        <span v-if="!isOrangeGranted" class="translucent hired-reward paragraph-orange">{{ hiredReward }}</span>
      </div>
    </div>
    <p v-if="item.job_title" class="job-title paragraph-s paragraph-dark-gray">{{ item.job_title }}</p>
    <div class="line tokens-line is-flex">
      <div class="is-flex">
        <div class="token-icons is-flex">
          <template v-for="(token, index) in allEarnedTokens">
            <clock-icon v-if="!token.id" :key="index"></clock-icon>
            <token-small v-else :type="token.type" :key="index"></token-small>
          </template>
        </div>
        <referral-status-badge :status="referralStatus"></referral-status-badge>
        <span class="referral-date paragraph-s paragraph-dark-gray">{{ formattedDate }}</span>
      </div>
      <div class="token-icons translucent is-flex">
        <token-small v-for="(type, index) in notEarnedTokenTypes" :key="index" :type="type"></token-small>
      </div>
    </div>
  </div>
</template>

<script>

import NeedReviewCircle from 'top20common/components/badges/NeedReviewCircle';
import ClockIcon from 'top20common/components/icons/ClockIcon';
import TokenSmall from 'top20common/components/icons/tokens/TokenSmall';
import {BRONZE, ORANGE, SILVER} from 'top20common/distribution/constants/tokens';
import currencies from 'top20common/distribution/services/currencies';
import formatter from 'top20common/distribution/services/formatter';
import {mapGetters} from 'vuex';

import ReferralStatusBadge from '@/common/badges/ReferralStatusBadge';
import TokenText from '@/common/token/TokenText';
import {STATUS_PENDING, STATUS_QUALIFIED} from '@/utils/constants/referrals';

export default {
  props: {
    item: Object,
    tokens: Array
  },
  data() {
    return {
      tokenStages: {
        [BRONZE]: 0,
        [SILVER]: 1,
        [ORANGE]: 2
      }
    };
  },
  computed: {
    ...mapGetters(['featureSwitchesState']),
    allEarnedTokens() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return this.tokens.sort((a, b) => {
        if (this.tokenStages[a.type] < this.tokenStages[b.type]) {
          return -1;
        }
        if (this.tokenStages[a.type] > this.tokenStages[b.type]) {
          return 1;
        }
        return 0;
      });
    },
    pendingEarnedTokens() {
      return this.tokens.filter(token => !token.id);
    },
    grantedEarnedTokens() {
      return this.tokens.filter(token => token.id);
    },
    earnedTokenTypes() {
      return this.allEarnedTokens.map(token => token.type);
    },
    notEarnedTokenTypes() {
      let tokenTypes = Object.keys(this.tokenStages);
      return tokenTypes.filter(value => !this.earnedTokenTypes.includes(value));
    },
    earnedTokenAmount() {
      if (!this.tokens || this.tokens.length === 0) {
        return '';
      }
      return currencies.getFormattedCurrencyFullValue(currencies.getCurrentCurrency(), this.item.earned, 0, 0);
    },
    isOrangeGranted() {
      return this.grantedEarnedTokens.filter(token => token.type === ORANGE).length;
    },
    hiredReward() {
      let getValue = number => currencies.getShortFormatNumber(number, 1);
      let currencyChar = currencies.getSimplifiedDisplayCurrency(currencies.getCurrentCurrency());
      return `${currencyChar}${getValue(this.item.total_earn_from)}-${getValue(this.item.total_earn_to)}`;
    },
    lastGrantedTokenType() {
      let lastGranted = this.grantedEarnedTokens[this.grantedEarnedTokens.length - 1];
      return lastGranted ? lastGranted.type : '';
    },
    formattedDate() {
      return this.$language.getFormattedDate(this.item.modified);
    },
    referralStatus() {
      if (this.pendingEarnedTokens.length) {
        return STATUS_PENDING;
      }
      return this.item.is_applied ? STATUS_QUALIFIED : this.item.status;
    }
  },
  methods: {
    handleReferralClick() {
      if (this.featureSwitchesState.REFERRAL_DETAILS) {
        this.$router.push({
          name: 'referral',
          params: {
            slug: this.item.slug,
            name: formatter.slugify(this.item.full_name)
          }
        });
      }
    }
  },
  components: {
    ReferralStatusBadge,
    TokenSmall,
    ClockIcon,
    TokenText,
    NeedReviewCircle
  }
};
</script>

<style lang="scss" scoped>
.referral-list-item {
  position: relative;
  padding: 0 20px;

  &.clickable {
    cursor: pointer;

    &:hover {
      .full-name {
        text-decoration: none;
      }
    }

    .full-name {
      text-decoration: underline;
    }
  }

  /deep/ .need-review-circle {
    position: absolute;
    left: 6px;
    top: 8px;
  }
}

.line {
  justify-content: space-between;
  align-items: flex-start;

  p.full-name {
    flex: 1;
    padding-right: 5px;
    max-width: 60%;
    overflow-wrap: break-word;
  }
}

.job-title {
  min-height: 23px;
}

.referral-date {
  margin-left: 5px;
  white-space: nowrap;
}

.token-amounts {
  padding-top: 4px;
  text-align: right;
}

.token-icons {
  white-space: nowrap;

  svg + svg {
    margin-left: 5px;
  }
}

.translucent {
  opacity: 0.5;
}

.tokens-line {
  margin: 5px 0;
}

.text-inline {
  display: inline;
}
</style>
